import { useState } from 'react';
import {
  AlertTriangle,
  Battery,
  Lightbulb,
  Link2,
  Send,
  Download,
  Plus,
  Radio,
  RefreshCw,
  Tag,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { toast } from '@/stores/toast-store';
import { useLocationStore } from '@/stores/location-store';
import { usePermissions } from '@/hooks/use-permissions';
import { usePersistentTab } from '@/hooks/use-persistent-tab';
import type { PermissionKey } from '@supernova/shared';
import {
  useBindLabel,
  usePushEslItem,
  useEslEvents,
  useEslGateways,
  useEslHealth,
  useEslLabels,
  useEslTemplates,
  useFlashLabel,
  useImportLabels,
  useBulkBindLabels,
  downloadEslLabelsCsv,
  useRegisterGateway,
  type EslLabel,
} from '@/hooks/use-esl';

// The API returns { error: { code, message } }. Those messages are written for
// the operator — the 12046 case literally tells them what to do next — so show
// them rather than a generic failure string.
function apiMessage(e: unknown, fallback: string): string {
  const msg = (e as { error?: { message?: string } })?.error?.message;
  return msg || fallback;
}

// API credentials are deliberately NOT here. Issuing one mints a caller that
// reaches our API and writes to real shelves, which is a platform decision
// rather than a tenant's — it lives on the business's super-admin page, and the
// service refuses the endpoint to anyone who is not a platform admin.
const TABS = ['Overview', 'Labels', 'Gateways', 'Activity'] as const;
type Tab = (typeof TABS)[number];

// Labels and Gateways each open with their own View. Overview and Activity
// come with the page, which opens with either.
const TAB_VIEW: Partial<Record<Tab, PermissionKey>> = {
  Labels: 'esl.labels.view',
  Gateways: 'esl.gateways.view',
};

export function EslPage() {
  // Deep-linkable as `#/esl?tab=Labels`.
  const [selectedTab, setTab] = usePersistentTab<Tab>(TABS, 'Overview');
  const permissions = usePermissions();
  const tabs = TABS.filter((t) => !TAB_VIEW[t] || permissions.has(TAB_VIEW[t]));
  const tab = tabs.includes(selectedTab) ? selectedTab : 'Overview';
  const { selectedLocationId } = useLocationStore();
  // 404 here means this location has no vendor-store mapping yet. The hardware
  // tabs all depend on it — the Gateways tab in particular offered a Register
  // form that could only ever fail — so they explain instead of presenting
  // controls that cannot work. API keys and Activity need no hardware, so they
  // stay usable.
  const { isError: notConnected, isLoading: checkingConnection } = useEslHealth(selectedLocationId);
  const needsStore = notConnected && !checkingConnection;

  if (!selectedLocationId) {
    return (
      <div className="p-6">
        <PageHeader title="Shelf Labels" description="Select a location to manage its labels." />
      </div>
    );
  }

  return (
    <div className="p-6">
      <PageHeader
        title="Shelf Labels"
        description="Register hardware, link labels to your products, and issue API credentials."
      />

      <div className="mb-6 flex gap-1 border-b border-border">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              tab === t ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {needsStore && tab !== 'Activity' ? (
        <NotConnected />
      ) : (
        <>
          {tab === 'Overview' && <OverviewTab locationId={selectedLocationId} />}
          {tab === 'Labels' && <LabelsTab locationId={selectedLocationId} />}
          {tab === 'Gateways' && <GatewaysTab locationId={selectedLocationId} />}
          {tab === 'Activity' && <ActivityTab />}
        </>
      )}
    </div>
  );
}

function NotConnected() {
  return (
    <div className="rounded-lg border border-dashed border-border p-10 text-center">
      <Tag className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
      <p className="font-medium">This location is not connected yet</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        Shelf labels are enabled for your business, but this location still needs to be linked to your label hardware.
        Your account manager does this once — contact support and the rest of this page will start working.
      </p>
      <p className="mx-auto mt-3 max-w-md text-xs text-muted-foreground">
        Activity is still available in the meantime.
      </p>
    </div>
  );
}

// ── Overview ───────────────────────────────────────────────────────────────

function OverviewTab({ locationId }: { locationId: string }) {
  const { data: health, isLoading } = useEslHealth(locationId);

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  // One notice for every hardware tab, so they cannot drift apart.
  if (!health) return <NotConnected />;

  return (
    <div className="space-y-6">
      {/* A gateway being down is not a device event — it stops every label in
          the building updating while everything else looks healthy. */}
      {!health.gatewayOnline && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <div>
            <p className="font-medium text-red-900">Gateway offline — labels in this store are not updating</p>
            <p className="mt-1 text-sm text-red-700">
              Prices already on the shelves stay as they are, but no change will reach them until the gateway is back.
              Check that it has power and a network connection.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Labels out of date"
          value={health.labelsOutOfDate}
          tone={health.labelsOutOfDate > 0 ? 'warn' : 'ok'}
          hint={health.labelsOutOfDate > 0 ? 'Showing something other than the latest price' : 'All shelves current'}
        />
        <Stat label="Waiting to send" value={health.queue.pending} />
        <Stat label="Awaiting confirmation" value={health.queue.sent} hint="Sent, not yet confirmed on the glass" />
        <Stat
          label="Failed"
          value={health.queue.failed + health.queue.dead}
          tone={health.queue.failed + health.queue.dead > 0 ? 'bad' : 'ok'}
        />
        <Stat label="Labels" value={health.tags.total} />
        <Stat
          label="Offline labels"
          value={health.tags.offline}
          hint="Asleep labels are normal — they wake on their own schedule"
        />
        <Stat
          label="Low battery"
          value={health.tags.low_battery}
          tone={health.tags.low_battery > 0 ? 'warn' : 'ok'}
          hint="Below 15%"
        />
        <Stat
          label="Gateway"
          value={health.gatewayOnline ? 'Online' : 'Offline'}
          tone={health.gatewayOnline ? 'ok' : 'bad'}
        />
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: number | string;
  hint?: string;
  tone?: 'ok' | 'warn' | 'bad' | 'neutral';
}) {
  const toneClass =
    tone === 'bad' ? 'text-red-600' : tone === 'warn' ? 'text-amber-600' : tone === 'ok' ? 'text-emerald-600' : '';
  return (
    <div className="rounded-lg border border-border bg-white p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${toneClass}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

// ── Labels ─────────────────────────────────────────────────────────────────

function LabelsTab({ locationId }: { locationId: string }) {
  const { data: labels, isLoading } = useEslLabels(locationId);
  const { data: templates } = useEslTemplates(locationId);
  const importLabels = useImportLabels();
  const bulkBind = useBulkBindLabels();
  const flash = useFlashLabel();
  const [macs, setMacs] = useState('');
  const [bulkResult, setBulkResult] = useState<{ bound: number; failed: number; errors: string[] } | null>(null);
  const [binding, setBinding] = useState<EslLabel | null>(null);
  const [testing, setTesting] = useState<EslLabel | null>(null);
  const permissions = usePermissions();
  // Import and link (a label with no product yet) are Create; relink, locate
  // and test send are Edit; the CSV is Export.
  const canCreate = permissions.has('esl.labels.create');
  const canEdit = permissions.has('esl.labels.edit');
  const canExport = permissions.has('esl.labels.export');

  // One box for both shapes:
  //   e100000a18de                          → import only
  //   e100000a18de,700000010982;700000010983 → import AND link, in one pass
  // Pasting a column from a spreadsheet is how a thousand labels get set up, so
  // the parser accepts what a spreadsheet produces rather than a strict format.
  const parseRows = () =>
    macs
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [mac, ...rest] = line.split(/[,\t]/).map((p) => p.trim());
        const itemKeys = rest
          .join(';')
          .split(';')
          .map((k) => k.trim())
          .filter(Boolean);
        return { mac, itemKeys };
      })
      .filter((r) => r.mac);

  const onImport = async () => {
    const rows = parseRows();
    if (!rows.length) return;
    setBulkResult(null);
    try {
      await importLabels.mutateAsync({ locationId, macs: rows.map((r) => r.mac) });

      // Link in the same pass for any row that named products. The layout is
      // chosen per label from its screen size, so nothing has to be picked.
      const toBind = rows.filter((r) => r.itemKeys.length > 0);
      if (toBind.length) {
        const res = await bulkBind.mutateAsync({ locationId, rows: toBind });
        setBulkResult({
          bound: res.bound,
          failed: res.failed,
          errors: res.results.filter((r) => !r.bound).map((r) => `${r.mac}: ${r.error ?? 'failed'}`),
        });
        toast.success(`${rows.length} imported, ${res.bound} linked${res.failed ? `, ${res.failed} failed` : ''}`);
      } else {
        toast.success(`${rows.length} label(s) imported`);
      }
      setMacs('');
    } catch (e) {
      toast.error(apiMessage(e, 'Import failed — check the MAC addresses'));
    }
  };

  return (
    <div className="space-y-6">
      {(canCreate || canExport) && (
        <div className="rounded-lg border border-border bg-white p-4">
          {canCreate && (
            <>
              <h3 className="mb-1 font-medium">Import labels</h3>
              <p className="mb-3 text-sm text-muted-foreground">
                Paste the MAC addresses printed on the labels, one per line. A new label can take a few minutes to
                appear — they report on their own schedule to save battery.
              </p>
              <textarea
                value={macs}
                onChange={(e) => setMacs(e.target.value)}
                rows={3}
                placeholder={'e1000007d7cc\ne100000bd86a,700000010982\ne10000a1bde0,700000010982;700000010983'}
                className="w-full rounded-md border border-border p-2 font-mono text-sm"
              />
            </>
          )}
          <div className={`flex items-center gap-2 ${canCreate ? 'mt-2' : ''}`}>
            {canCreate && (
              <button
                onClick={onImport}
                disabled={importLabels.isPending || bulkBind.isPending || !macs.trim()}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                <Plus className="h-4 w-4" /> Import
              </button>
            )}
            {/* Export gives back exactly the columns Import accepts, so the round
              trip through a spreadsheet is the bulk-editing workflow. */}
            {canExport && (
              <button
                onClick={() => downloadEslLabelsCsv(locationId).catch(() => toast.error('Export failed'))}
                className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
              >
                <Download className="h-4 w-4" /> Export CSV
              </button>
            )}
          </div>

          {/* Per row, because at this size "which ones failed" is the only useful
            answer — and the rest did succeed. */}
          {bulkResult && bulkResult.failed > 0 && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="font-medium">
                {bulkResult.bound} linked, {bulkResult.failed} could not be:
              </p>
              <ul className="mt-1 list-inside list-disc space-y-0.5">
                {bulkResult.errors.slice(0, 8).map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
              {bulkResult.errors.length > 8 && <p className="mt-1">…and {bulkResult.errors.length - 8} more.</p>}
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !labels?.length ? (
        <EmptyState
          icon={Tag}
          title="No labels yet"
          body={canCreate ? 'Import the MAC addresses above to get started.' : 'No labels have been imported here yet.'}
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40 text-left">
              <tr>
                <th className="p-3 font-medium">Label</th>
                <th className="p-3 font-medium">Linked to</th>
                <th className="p-3 font-medium">Battery</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium">Linked by</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {labels.map((l) => (
                <tr key={l.mac} className="border-b border-border last:border-0">
                  <td className="p-3 font-mono text-xs">{l.mac}</td>
                  <td className="p-3">
                    {l.itemKey ? (
                      <span className="font-medium">{l.itemKey}</span>
                    ) : (
                      <span className="text-amber-600">Not linked</span>
                    )}
                  </td>
                  <td className="p-3">
                    {l.battery == null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <span className={l.battery < 15 ? 'text-amber-600' : ''}>
                        <Battery className="mr-1 inline h-4 w-4" />
                        {l.battery}%
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    {l.online === 2 ? (
                      <span className="text-emerald-600">Online</span>
                    ) : (
                      <span className="text-muted-foreground">Asleep</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {l.boundBy
                      ? `${l.boundBy}${l.boundAt ? ` · ${new Date(l.boundAt).toLocaleDateString()}` : ''}`
                      : '—'}
                  </td>
                  <td className="p-3 text-right">
                    {canEdit && (
                      <button
                        onClick={async () => {
                          try {
                            await flash.mutateAsync({ mac: l.mac, locationId });
                            toast.success('Label is flashing on the shelf');
                          } catch {
                            toast.error('Could not flash the label');
                          }
                        }}
                        title="Flash this label's light so you can find it on the shelf"
                        className="mr-2 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs"
                      >
                        <Lightbulb className="h-3 w-3" /> Locate
                      </button>
                    )}
                    {(l.itemKey ? canEdit : canCreate) && (
                      <button
                        onClick={() => setBinding(l)}
                        className="mr-2 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs"
                      >
                        <Link2 className="h-3 w-3" /> {l.itemKey ? 'Relink' : 'Link'}
                      </button>
                    )}
                    {canEdit && (
                      <button
                        onClick={() => setTesting(l)}
                        disabled={!l.itemKey}
                        title={
                          l.itemKey
                            ? 'Send a price through the real pipeline and watch this label redraw'
                            : 'Link this label to a product first'
                        }
                        className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs disabled:opacity-40"
                      >
                        <Send className="h-3 w-3" /> Test
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {binding && (
        <BindDialog
          label={binding}
          locationId={locationId}
          templates={templates ?? []}
          canLocate={canEdit}
          onClose={() => setBinding(null)}
        />
      )}

      {testing && <TestPriceDialog label={testing} locationId={locationId} onClose={() => setTesting(null)} />}
    </div>
  );
}

function BindDialog({
  label,
  locationId,
  templates,
  canLocate,
  onClose,
}: {
  label: EslLabel;
  locationId: string;
  templates: Array<{ id: string; name: string; inch?: number; colour?: string }>;
  /** Flashing a label is Edit, which a Create-only holder may lack. */
  canLocate: boolean;
  onClose: () => void;
}) {
  const bind = useBindLabel();
  const flash = useFlashLabel();
  const [itemKey, setItemKey] = useState(label.itemKey ?? '');
  const [templateId, setTemplateId] = useState(label.templateId ?? templates[0]?.id ?? '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <h3 className="mb-1 text-lg font-semibold">Link label {label.mac}</h3>

        {/* Mis-linking is the one failure the platform cannot detect: the label
            will confidently show a price for the wrong product, indefinitely. */}
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Make sure this is the right label on the shelf. Use <strong>Locate</strong> to flash it — a label linked to
            the wrong product shows a wrong price and nothing can detect it.
          </p>
        </div>

        {/* The platform will not attach a tag to a product it has never heard
            of. Rather than making the operator discover that ordering, the
            server creates the product as part of linking. Nothing appears on
            the shelf from that — an unbound tag displays nothing. */}
        <p className="mb-4 text-xs text-muted-foreground">
          New identifiers are fine — the product is created as part of linking. Send a price afterwards and the label
          will show it.
        </p>

        {canLocate && (
          <button
            onClick={() =>
              flash.mutateAsync({ mac: label.mac, locationId }).catch(() => toast.error('Could not flash'))
            }
            className="mb-4 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs"
          >
            <Lightbulb className="h-3 w-3" /> Locate this label
          </button>
        )}

        <label className="mb-1 block text-sm font-medium">Your product identifier</label>
        <input
          value={itemKey}
          onChange={(e) => setItemKey(e.target.value)}
          placeholder="SKU, PLU or barcode"
          className="mb-1 w-full rounded-md border border-border p-2 text-sm"
        />
        <p className="mb-4 text-xs text-muted-foreground">
          Whatever your own system uses. This is the value you send when updating the price.
        </p>

        <label className="mb-1 block text-sm font-medium">Layout</label>
        <select
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          className="mb-4 w-full rounded-md border border-border p-2 text-sm"
        >
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} {t.inch ? `· ${t.inch}"` : ''} {t.colour ?? ''}
            </option>
          ))}
        </select>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-border px-3 py-2 text-sm">
            Cancel
          </button>
          <button
            disabled={!itemKey.trim() || bind.isPending}
            onClick={async () => {
              try {
                await bind.mutateAsync({ mac: label.mac, locationId, itemKey: itemKey.trim(), templateId });
                toast.success('Label linked — it should redraw shortly');
                onClose();
              } catch (e) {
                toast.error(apiMessage(e, 'Link failed — check the layout matches this label’s size'));
              }
            }}
            className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            Link label
          </button>
        </div>
      </div>
    </div>
  );
}

// Sends one item down the SAME path an external POS uses — POST /items, then
// the queue, the per-store pacing, the vendor, and the poller that confirms.
// Nothing here shortcuts to the hardware, so a shelf that changes proves the
// whole Flow B chain rather than a test harness pretending to.
function TestPriceDialog({ label, locationId, onClose }: { label: EslLabel; locationId: string; onClose: () => void }) {
  const push = usePushEslItem();
  const [name, setName] = useState('Test Item');
  // A different price each time on purpose: re-sending the same content is
  // correctly skipped as unchanged, which looks like nothing happened.
  const [price, setPrice] = useState(() => (Math.random() * 9 + 1).toFixed(2));
  // Unit price is its own field on the label, not part of the price: shelf-edge
  // labels show "£4.99" and "£2.50 / L" separately, and shoppers compare on the
  // second one.
  const [unitPrice, setUnitPrice] = useState('');
  const [sent, setSent] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <h3 className="mb-1 text-lg font-semibold">Send a test price</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Goes to <span className="font-medium">{label.itemKey}</span> on label{' '}
          <span className="font-mono text-xs">{label.mac}</span>. Watch the shelf — e-paper takes a few seconds to
          redraw, and a sleeping label waits for its next check-in.
        </p>

        <label className="mb-1 block text-sm font-medium">Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mb-3 w-full rounded-md border border-border p-2 text-sm"
        />

        <label className="mb-1 block text-sm font-medium">Price</label>
        <input
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="mb-3 w-full rounded-md border border-border p-2 text-sm"
        />

        <label className="mb-1 block text-sm font-medium">Unit price</label>
        <input
          value={unitPrice}
          onChange={(e) => setUnitPrice(e.target.value)}
          placeholder="e.g. $2.50 / L"
          className="mb-1 w-full rounded-md border border-border p-2 text-sm"
        />
        <p className="mb-4 text-xs text-muted-foreground">
          Optional. Shown as its own line on the label — leave blank to leave it unchanged.
        </p>

        {sent && (
          <div className="mb-4 rounded-md bg-emerald-50 p-3 text-xs text-emerald-800">
            Accepted. Watch the Activity tab — it moves to <strong>Shelf updated</strong> only once the platform
            confirms the label actually redrew.
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-border px-3 py-2 text-sm">
            Close
          </button>
          <button
            disabled={push.isPending || !price.trim()}
            onClick={async () => {
              try {
                const res = await push.mutateAsync({
                  locationId,
                  itemKey: label.itemKey!,
                  fields: {
                    name: name.trim(),
                    price: price.trim(),
                    // Omitted rather than sent empty: an empty string would
                    // overwrite whatever the label currently shows.
                    ...(unitPrice.trim() ? { unit: unitPrice.trim() } : {}),
                  },
                });
                if (res.skipped > 0 && res.accepted === 0) {
                  toast.info('Unchanged — the shelf already shows this, so nothing was sent');
                } else {
                  toast.success('Sent');
                  setSent(true);
                }
              } catch (e) {
                toast.error(apiMessage(e, 'Could not send — is this location linked to a store?'));
              }
            }}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            <Send className="h-4 w-4" /> Send
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Gateways ───────────────────────────────────────────────────────────────

function GatewaysTab({ locationId }: { locationId: string }) {
  const [refresh, setRefresh] = useState(false);
  const { data: gateways, isLoading, refetch } = useEslGateways(locationId, refresh);
  const register = useRegisterGateway();
  const canRegister = usePermissions().has('esl.gateways.create');
  const [mac, setMac] = useState('');
  const [name, setName] = useState('');

  return (
    <div className="space-y-6">
      {canRegister && (
        <div className="rounded-lg border border-border bg-white p-4">
          <h3 className="mb-1 font-medium">Register a gateway</h3>
          <p className="mb-3 text-sm text-muted-foreground">
            The MAC address is printed on the base of the unit. It should come online within a minute of being powered
            and connected.
          </p>
          <div className="flex flex-wrap gap-2">
            <input
              value={mac}
              onChange={(e) => setMac(e.target.value)}
              placeholder="ac233fc291a5"
              className="flex-1 rounded-md border border-border p-2 font-mono text-sm"
            />
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (optional)"
              className="flex-1 rounded-md border border-border p-2 text-sm"
            />
            <button
              disabled={!mac.trim() || register.isPending}
              onClick={async () => {
                try {
                  await register.mutateAsync({ locationId, mac: mac.trim(), name: name.trim() || undefined });
                  toast.success('Gateway registered');
                  setMac('');
                  setName('');
                } catch (e) {
                  toast.error(apiMessage(e, 'Could not register — check the MAC address'));
                }
              }}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              <Plus className="h-4 w-4" /> Register
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={() => {
            setRefresh(true);
            refetch();
          }}
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm"
        >
          <RefreshCw className="h-4 w-4" /> Check live status
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !gateways?.length ? (
        <EmptyState icon={Radio} title="No gateway registered" body="Labels cannot update until one is online." />
      ) : (
        <div className="space-y-3">
          {gateways.map((g) => (
            <div key={g.mac} className="flex items-center justify-between rounded-lg border border-border bg-white p-4">
              <div className="flex items-center gap-3">
                {g.online === 1 ? (
                  <Wifi className="h-5 w-5 text-emerald-600" />
                ) : (
                  <WifiOff className="h-5 w-5 text-red-600" />
                )}
                <div>
                  <p className="font-medium">{g.name || g.mac}</p>
                  <p className="font-mono text-xs text-muted-foreground">{g.mac}</p>
                </div>
              </div>
              <div className="text-right text-sm">
                <p className={g.online === 1 ? 'text-emerald-600' : 'text-red-600'}>
                  {g.online === 1 ? 'Online' : 'Offline'}
                </p>
                {/* Duration, not the last poll — how long the store has been blind. */}
                {g.online !== 1 && g.offlineSince && (
                  <p className="text-xs text-red-600">since {new Date(g.offlineSince).toLocaleString()}</p>
                )}
                {g.firmware && <p className="text-xs text-muted-foreground">firmware {g.firmware}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── API keys ───────────────────────────────────────────────────────────────

// ── Activity ───────────────────────────────────────────────────────────────

function ActivityTab() {
  const { data: events, isLoading } = useEslEvents(150);

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!events?.length)
    return <EmptyState icon={Tag} title="Nothing yet" body="Activity appears here as labels update." />;

  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-white">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/40 text-left">
          <tr>
            <th className="p-3 font-medium">When</th>
            <th className="p-3 font-medium">Event</th>
            <th className="p-3 font-medium">Product</th>
            <th className="p-3 font-medium">Label</th>
            <th className="p-3 font-medium">By</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id} className="border-b border-border last:border-0">
              <td className="p-3 text-xs text-muted-foreground">{new Date(e.createdAt).toLocaleString()}</td>
              <td className="p-3">
                <EventBadge type={e.type} />
              </td>
              <td className="p-3 text-xs">{e.itemKey ?? '—'}</td>
              <td className="p-3 font-mono text-xs">{e.mac ?? '—'}</td>
              <td className="p-3 text-xs text-muted-foreground">{e.actor ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EventBadge({ type }: { type: string }) {
  const tone =
    type.endsWith('.confirmed') || type.endsWith('.bound') || type.endsWith('.registered')
      ? 'bg-emerald-50 text-emerald-700'
      : type.endsWith('.failed') || type.endsWith('.dead') || type === 'gateway.offline'
        ? 'bg-red-50 text-red-700'
        : 'bg-muted text-muted-foreground';
  const readable: Record<string, string> = {
    'label.accepted': 'Price received',
    'label.confirmed': 'Shelf updated',
    'label.failed': 'Failed',
    'label.dead': 'Given up',
    'label.bound': 'Label linked',
    'gateway.registered': 'Gateway added',
    'gateway.offline': 'Gateway offline',
    'store.onboarded': 'Store set up',
  };
  return <span className={`rounded px-2 py-0.5 text-xs ${tone}`}>{readable[type] ?? type}</span>;
}

function EmptyState({ icon: Icon, title, body }: { icon: typeof Tag; title: string; body: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-10 text-center">
      <Icon className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
      <p className="font-medium">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
